import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '../lib/supabase'

interface Props {
  bowl: readonly string[]
  onAddItem: (text: string) => void
  onClose: () => void
}

interface Category {
  id: string
  name: string
}

const PAGE_SIZE = 6

export default function SuggestIdeasModal({ bowl, onAddItem, onClose }: Props) {
  const [phase, setPhase] = useState<'categories' | 'items'>('categories')
  const [category, setCategory] = useState<Category | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [items, setItems] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [added, setAdded] = useState<Set<string>>(new Set())

  const bowlSet = new Set(bowl.map((b) => b.toLowerCase()))

  async function loadCategories() {
    setLoading(true)
    setError(null)
    const { data, error } = await supabase.rpc('suggest_categories', { limit_count: PAGE_SIZE })
    setLoading(false)
    if (error) {
      setError('Could not load categories — the idea bank may not be set up yet.')
      return
    }
    setCategories((data as Category[]) ?? [])
  }

  async function loadItems(catId: string) {
    setLoading(true)
    setError(null)
    const { data, error } = await supabase.rpc('suggest_items', { category: catId, limit_count: PAGE_SIZE })
    setLoading(false)
    if (error) {
      setError('Could not load suggestions for that category.')
      return
    }
    setItems(((data as { text: string }[]) ?? []).map((r) => r.text))
  }

  useEffect(() => {
    void loadCategories()
  }, [])

  const pickCategory = (c: Category) => {
    setCategory(c)
    setPhase('items')
    void loadItems(c.id)
  }

  const backToCategories = () => {
    setCategory(null)
    setPhase('categories')
    setItems([])
  }

  const handleAdd = (text: string) => {
    if (bowlSet.has(text.toLowerCase())) return
    onAddItem(text)
    setAdded((prev) => {
      const next = new Set(prev)
      next.add(text.toLowerCase())
      return next
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-warmblack/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3 pt-12"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-cream rounded-2xl shadow-2xl max-w-md w-full max-h-full flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-warmblack/10">
          <div className="flex items-center gap-2">
            {phase === 'items' && (
              <button
                onClick={backToCategories}
                aria-label="Back"
                className="w-8 h-8 rounded-lg hover:bg-warmblack/5 text-warmblack/60 text-xl flex items-center justify-center"
              >
                ‹
              </button>
            )}
            <h2 className="font-display text-xl font-bold text-warmblack">
              {phase === 'categories' ? 'Need ideas?' : category?.name}
            </h2>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-warmblack/5 text-warmblack/60 text-2xl flex items-center justify-center"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {error && (
            <p className="font-body text-sm text-coral bg-coral/10 rounded-lg px-3 py-2">{error}</p>
          )}

          {phase === 'categories' && (
            <>
              <p className="font-body text-sm text-warmblack/60 mb-3">
                Pick a category to see suggestions you can drop straight into the bowl.
              </p>
              {loading && categories.length === 0 ? (
                <SkeletonRows />
              ) : (
                categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => pickCategory(c)}
                    className="w-full text-left bg-white hover:bg-warmblack/5 border border-warmblack/10 rounded-xl px-4 py-3 font-body text-base font-bold text-warmblack transition-colors"
                  >
                    {c.name}
                  </button>
                ))
              )}
            </>
          )}

          {phase === 'items' && (
            <>
              <p className="font-body text-sm text-warmblack/60 mb-3">
                Tap an item to add it to the bowl.
              </p>
              {loading && items.length === 0 ? (
                <SkeletonRows />
              ) : (
                items.map((it) => {
                  const isInBowl = bowlSet.has(it.toLowerCase())
                  const justAdded = added.has(it.toLowerCase())
                  return (
                    <button
                      key={it}
                      onClick={() => handleAdd(it)}
                      disabled={isInBowl || justAdded}
                      className={`w-full text-left rounded-xl border px-4 py-3 font-body text-base transition-colors ${
                        isInBowl || justAdded
                          ? 'bg-sage/20 border-sage/30 text-warmblack/70 cursor-default'
                          : 'bg-white border-warmblack/10 hover:bg-warmblack/5 text-warmblack'
                      }`}
                    >
                      <span className="font-bold">{it}</span>
                      {(isInBowl || justAdded) && (
                        <span className="ml-2 text-xs text-sage font-bold">✓ in bowl</span>
                      )}
                    </button>
                  )
                })
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-warmblack/10 flex justify-between gap-2">
          <button
            onClick={phase === 'categories' ? loadCategories : () => category && loadItems(category.id)}
            disabled={loading}
            className="bg-white hover:bg-warmblack/5 disabled:opacity-50 text-warmblack font-display font-bold px-4 py-2 rounded-xl border border-warmblack/10 transition-colors text-sm"
          >
            {loading ? 'Loading…' : phase === 'categories' ? 'More categories' : 'More options'}
          </button>
          <button
            onClick={onClose}
            className="bg-coral hover:bg-coral/90 text-white font-display font-bold px-4 py-2 rounded-xl transition-colors text-sm"
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: PAGE_SIZE }).map((_, i) => (
        <div key={i} className="w-full bg-white/50 border border-warmblack/5 rounded-xl px-4 py-3 animate-pulse">
          <div className="h-4 bg-warmblack/10 rounded w-2/3" />
        </div>
      ))}
    </>
  )
}
